import max from 'lodash/max';
import { addDays, addHours, addMonths, minutesToMilliseconds } from 'date-fns';
import { colors } from '@atlaskit/theme';
import { getTokenValue } from '@atlaskit/tokens';
import type { MessageDescriptorV2 as MessageDescriptor } from '@atlassian/jira-intl/src/v2/types.tsx';
import { floatDifferenceOfDates } from '@atlassian/jira-platform-utils-date-fns/src/main.tsx';
import type { SummaryResponse, CustomSummaryResponse, MarkLine, MinMax } from '../types';
import {
	TYPE_STAR,
	TYPE_PERCENTAGE,
	TYPE_TIME_DECIMAL_PLACES,
	TYPE_STAR_DECIMAL_PLACES,
	TYPE_PERCENTAGE_DECIMAL_PLACES,
	TYPE_TIME,
	VALUE_TYPES,
	PERCENTAGE_TYPE,
} from './constants';
import messages from './messages';

export type YAxisData = {
	value: string;
	yAxisValue: number;
	valueType: string;
};

export type FormatMessage = (
	messageDescriptor: MessageDescriptor,
	options?: {
		[key: string]: string | number;
	},
) => string;

const minimumInterval = 5;
const fiveMinutesInMilliSeconds = minutesToMilliseconds(minimumInterval);

export const formatToMonth = (value: number): YAxisData => ({
	yAxisValue: value,
	value: floatDifferenceOfDates(new Date(value), new Date(0), 'months')
		.toFixed(TYPE_TIME_DECIMAL_PLACES)
		.toString(),
	valueType: VALUE_TYPES.months,
});

export const formatToDays = (value: number): YAxisData => ({
	yAxisValue: value,
	value: floatDifferenceOfDates(new Date(value), new Date(0), 'days')
		.toFixed(TYPE_TIME_DECIMAL_PLACES)
		.toString(),
	valueType: VALUE_TYPES.days,
});

export const formatToHours = (value: number): YAxisData => ({
	yAxisValue: value,
	value: floatDifferenceOfDates(new Date(value), new Date(0), 'hours')
		.toFixed(TYPE_TIME_DECIMAL_PLACES)
		.toString(),
	valueType: VALUE_TYPES.hours,
});

export const formatToMinutes = (value: number): YAxisData => ({
	yAxisValue: value,
	value: floatDifferenceOfDates(new Date(value), new Date(0), 'minutes')
		.toFixed(TYPE_TIME_DECIMAL_PLACES)
		.toString(),
	valueType: VALUE_TYPES.minutes,
});

export const formatStar = (value: number) => ({
	yAxisValue: value,
	value: value.toFixed(TYPE_STAR_DECIMAL_PLACES),
	valueType: '',
});

export const formatPercentage = (value: number) => ({
	yAxisValue: value,
	value: (value * 100).toFixed(TYPE_PERCENTAGE_DECIMAL_PLACES),
	valueType: VALUE_TYPES.percentage,
});

export const defaultFormat = (value: number) => ({
	yAxisValue: value,
	value: value.toString(),
	valueType: '',
});

export const getMaxYAxisValue = (source: SummaryResponse | CustomSummaryResponse): number => {
	const eachSeriesMaxValues = source.series.map(({ data }) => max(data.map(({ y }) => y)));
	// @ts-expect-error - TS2322 - Type 'number | undefined' is not assignable to type 'number'.
	return max(eachSeriesMaxValues);
};

export const getFormattedLabel = (type: string, value: string, formatMessage: FormatMessage) => {
	// @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ percentage: MessageDescriptorV2; minutes: MessageDescriptorV2; hours: MessageDescriptorV2; days: MessageDescriptorV2; months: MessageDescriptorV2; }'.
	if (messages[type]) {
		// @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ percentage: MessageDescriptorV2; minutes: MessageDescriptorV2; hours: MessageDescriptorV2; days: MessageDescriptorV2; months: MessageDescriptorV2; }'.
		return `${value} ${formatMessage(messages[type])}`;
	}

	return value;
};

export const getTimeFormat = (maxYValue: number) => {
	if (maxYValue > addMonths(new Date(0), 6).getTime()) {
		return formatToMonth;
	}
	if (maxYValue > addDays(new Date(0), 30).getTime()) {
		return formatToDays;
	}
	if (maxYValue > addHours(new Date(0), 2).getTime()) {
		return formatToHours;
	}
	return formatToMinutes;
};

export const getYAxisType = (source: SummaryResponse | CustomSummaryResponse): string => {
	if (source.series.length > 0) {
		// all series in a report have the same y-axis type
		return source.series[0].yAxisType;
	}
	return '';
};

/**
 * getYAxisMinMax
 * if data type is percentage type then set the default's to min: 0, max: 100
 * if data type is time type and less than 5 min's then set the default's to min: 1, max: 5mins
 * else min interval should be 5.
 */
export const getYAxisMinMax = (source: SummaryResponse | CustomSummaryResponse): MinMax => {
	const yAxisType = getYAxisType(source);
	let maxInterval;
	switch (yAxisType) {
		case TYPE_TIME: {
			const maxYValue = getMaxYAxisValue(source);
			if (maxYValue <= fiveMinutesInMilliSeconds) {
				maxInterval = minimumInterval;
			}
			return { min: 0, max: maxInterval };
		}
		case TYPE_PERCENTAGE:
			return { min: 0, max: 100 };
		default: {
			const maxYValue = getMaxYAxisValue(source);
			if (maxYValue <= minimumInterval) {
				maxInterval = minimumInterval;
			}
			return { min: 0, max: maxInterval };
		}
	}
};

export const getValueFormatter = (
	source: SummaryResponse | CustomSummaryResponse,
): ((arg1: number) => YAxisData) => {
	const yAxisType = getYAxisType(source);
	switch (yAxisType) {
		case TYPE_TIME: {
			const maxYValue = getMaxYAxisValue(source);
			return getTimeFormat(maxYValue);
		}
		case TYPE_STAR:
			return formatStar;
		case TYPE_PERCENTAGE:
			return formatPercentage;
		default:
			return defaultFormat;
	}
};

export const isPercentageType = (type: string) => type === PERCENTAGE_TYPE;

// Only charts of percentage type series can have a target
export const isPercentageChart = (source: CustomSummaryResponse): boolean =>
	// @ts-expect-error - TS2339 - Property 'seriesType' does not exist on type 'SeriesEntity'.
	source.series.some((_series) => isPercentageType(_series.seriesType.yaxis.typeKey));

export const createMarkLine = (target: number) => ({
	label: {
		formatter: `${target}%`,
		color: getTokenValue('color.chart.neutral', colors.N800),
	},
	silent: true,
	lineStyle: {
		color: getTokenValue('color.chart.neutral', colors.N800),
	},
	animation: false,
	data: [
		{
			name: 'Target',
			yAxis: target,
		},
	],
});

export const getTargetMarkLine = (source: CustomSummaryResponse): MarkLine | undefined => {
	if (isPercentageChart(source) && source.target !== undefined && source.target !== 0) {
		return createMarkLine(source.target);
	}

	return undefined;
};
